import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Container,
    Typography,
    IconButton,
    Button,
    Menu,
    MenuItem,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    CalendarIcon,
    CustomDownloadIcon,
    CustomRefreshIcon,
    UsersIcon,
    ActivityIcon,
    FolderIcon,
    SettingsIcon,
    GlobeIcon,
    LiveCamIcon,
    MonitorIcon,
} from '../../Icons/DashboardIcons';
import UserStatisticsTab from './UserStatisticsTab';
import EngagementTab from './EngagementTab';
import MachineUsageTab from './MachineUsageTab';
import ProjectsTab from './ProjectsTab';
import MISDataTab from './MISDataTab';
import LiveSimulationTab from './LiveSimulationTab';
import TLIndividualLiveFeed from './TLIndividualLiveFeed';
import SimulationAPIs from '../../utilities/api/simulation/SimulationAPIs';
import TLDashboardAPIs from '../../utilities/api/dashboard/TLDashboardAPIs';
import MBFDashboardAPIs from '../../utilities/api/dashboard/MBFDashboardAPIs';

const TLDashboardContent = () => {
    const { user } = useSelector(state => state.user.user);
    const [lastUpdated, setLastUpdated] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('Weekly');
    const [activeTab, setActiveTab] = useState('User Statistics');
    const [refreshKey, setRefreshKey] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [selectedSiteId, setSelectedSiteId] = useState(null);
    const [tlList, setTlList] = useState([]);
    const [isSimulation, setIsSimulation] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Fetch TL list on mount
    useEffect(() => {
        const fetchTLs = async () => {
            try {
                const response = await MBFDashboardAPIs.getTLList();
                console.log('TLDashboardContent - Fetched TL List:', response);
                setTlList(response || []);
            } catch (error) {
                console.error('Error fetching TL list:', error);
            }
        };
        fetchTLs();
    }, []);

    // Watch for TL_id changes and update selectedSiteId
    useEffect(() => {
        if (user) {
            let tlId = user?.TL?.id || user?.TL_id;

            console.log('TLDashboardContent - Raw TL Info:', {
                TL_id: user?.TL_id,
                TL_name: user?.TL_name,
                TL_obj: user?.TL,
                resolved_tlId: tlId,
                type: typeof tlId,
                tlListLength: tlList.length
            });

            // If TL_id is not available but we have TL_name, try to find the ID from the TL list
            if ((!tlId || tlId === '') && user?.TL_name && tlList.length > 0) {
                const matchingTL = tlList.find(tl => tl.name === user.TL_name);
                if (matchingTL) {
                    console.log('TLDashboardContent - Resolved TL_id from name:', user.TL_name, '->', matchingTL.id);
                    tlId = matchingTL.id;
                }
            }

            // Parse the TL ID - handle both string and number
            let parsedTlId = null;
            if (tlId !== null && tlId !== undefined && tlId !== '') {
                parsedTlId = typeof tlId === 'number' ? tlId : parseInt(tlId, 10);

                // Check if parsing resulted in NaN
                if (isNaN(parsedTlId)) {
                    console.error('TLDashboardContent - Invalid TL_id, cannot parse:', tlId);
                    parsedTlId = null;
                }
            }

            // Update selectedSiteId whenever TL changes
            if (parsedTlId && parsedTlId !== selectedSiteId) {
                console.log('TLDashboardContent - Setting selectedSiteId to:', parsedTlId);
                setSelectedSiteId(parsedTlId);
            }
        }
    }, [user?.TL_id, user?.TL?.id, user?.TL_name, tlList]); // Watch for changes in TL

    useEffect(() => {
        updateTimestamp();

        const checkSimulation = async () => {
            try {
                const status = await SimulationAPIs.getStatus();
                console.log('TLDashboardContent - Simulation status:', status);
                setIsSimulation(status.is_simulation);
            } catch (error) {
                console.error('Error checking simulation status:', error);
            }
        };
        checkSimulation();
    }, []);

    const updateTimestamp = () => {
        const now = new Date();
        const options = {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        };
        const formattedDate = now.toLocaleString('en-US', options).replace(',', '');
        setLastUpdated(formattedDate);
    };

    const handleMenuOpen = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = period => {
        if (period) {
            setSelectedPeriod(period);
        }
        setAnchorEl(null);
    };

    const handleRefresh = () => {
        updateTimestamp();
        setRefreshKey(prev => prev + 1);
    };

    // Map active tab to API data_type parameter
    const getDataTypeFromTab = (tab) => {
        switch (tab) {
            case 'User Statistics': return 'user-statistics';
            case 'Engagement': return 'engagement';
            case 'Projects': return 'projects';
            case 'Machine Usage': return 'machine-usage';
            case 'MIS Data': return 'mis-data';
            default: return 'user-statistics';
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const dataType = getDataTypeFromTab(activeTab);
            const period = selectedPeriod.toLowerCase();
            const blob = await TLDashboardAPIs.exportCSV(dataType, period);

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `tl-dashboard-${dataType}-${period}-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('Failed to download CSV. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box
            sx={{
                flexGrow: 1,
                overflowY: 'auto',
                bgcolor: '#ffffff',
                p: { xs: 1.5, sm: 2, md: 3 },
            }}
        >
            <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
                {/* Header Content */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                        borderRadius: { xs: '6px', md: '8px' },
                        padding: { xs: '12px 16px', sm: '14px 20px', md: '16px 24px' },
                        mb: { xs: 2, md: 3 },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1.5, sm: 2 },
                    }}
                >
                    {/* Left side - Title and Date Badge */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, md: 2 },
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: '18px', sm: '20px', md: '24px' },
                                fontWeight: 500,
                                fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                                color: '#333333',
                                lineHeight: '32px',
                            }}
                        >
                            Dashboard
                        </Typography>
                        {isSimulation && (
                            <Chip
                                icon={<MonitorIcon color="#fff" />}
                                label="Simulation Mode"
                                size="small"
                                sx={{
                                    bgcolor: '#f50057',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    borderRadius: '4px',
                                    '& .MuiChip-icon': { color: 'inherit', fontSize: '16px' },
                                    mr: 1
                                }}
                            />
                        )}
                        <Box
                            sx={{
                                bgcolor: '#f2f2f2',
                                borderRadius: '12px',
                                padding: { xs: '3px 6px', md: '4px 8px' },
                                display: { xs: 'none', sm: 'inline-flex' },
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: '10px', md: '12px' },
                                    fontWeight: 400,
                                    fontFamily: '"Roboto-Regular", "Roboto", sans-serif',
                                    color: '#8c8c8c',
                                    lineHeight: '16px',
                                }}
                            >
                                Last Updated on: {lastUpdated}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right side - Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: { xs: '8px', md: '12px' },
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            variant="outlined"
                            endIcon={<CalendarIcon />}
                            onClick={handleMenuOpen}
                            sx={{
                                textTransform: 'none',
                                fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                                fontSize: { xs: '12px', md: '14px' },
                                fontWeight: 500,
                                lineHeight: '20px',
                                color: '#8c8c8c',
                                bgcolor: '#ffffff',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.05)',
                                padding: { xs: '6px 10px', md: '8px 12px' },
                                minWidth: { xs: 'auto', md: '100px' },
                                '&:hover': {
                                    bgcolor: '#f9f9f9',
                                    border: '1px solid #e6e6e6',
                                },
                            }}
                        >
                            {selectedPeriod}
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => handleMenuClose(null)}
                        >
                            <MenuItem
                                onClick={() => handleMenuClose('Weekly')}
                                sx={{ color: '#8C8C8C' }}
                            >
                                Weekly
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleMenuClose('Monthly')}
                                sx={{ color: '#8C8C8C' }}
                            >
                                Monthly
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleMenuClose('Yearly')}
                                sx={{ color: '#8C8C8C' }}
                            >
                                Yearly
                            </MenuItem>
                        </Menu>

                        <IconButton
                            onClick={handleDownload}
                            disabled={downloading}
                            sx={{
                                bgcolor: '#ffffff',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                padding: { xs: '6px', md: '8px' },
                                width: { xs: '32px', md: '36px' },
                                height: { xs: '32px', md: '36px' },
                                '&:hover': {
                                    bgcolor: '#f9f9f9',
                                },
                                '&:disabled': {
                                    bgcolor: '#f5f5f5',
                                },
                            }}
                        >
                            {downloading ? (
                                <CircularProgress size={16} sx={{ color: '#666' }} />
                            ) : (
                                <CustomDownloadIcon />
                            )}
                        </IconButton>

                        <IconButton
                            onClick={handleRefresh}
                            sx={{
                                bgcolor: '#ffffff',
                                border: '1px solid #e6e6e6',
                                borderRadius: '8px',
                                padding: { xs: '6px', md: '8px' },
                                width: { xs: '32px', md: '36px' },
                                height: { xs: '32px', md: '36px' },
                                '&:hover': {
                                    bgcolor: '#f9f9f9',
                                },
                            }}
                        >
                            <CustomRefreshIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Tabs Section */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: { xs: '8px', md: '12px' },
                        padding: { xs: '6px', md: '8px' },
                        mb: { xs: 2, md: 3 },
                        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: { xs: '8px', sm: '12px', md: '20px' },
                            alignItems: 'center',
                            minWidth: 'max-content',
                        }}
                    >
                        {/* User Statistics Tab */}
                        <Box
                            onClick={() => setActiveTab('User Statistics')}
                            sx={{
                                borderBottom: activeTab === 'User Statistics' ? '2px solid #007bff' : 'none',
                                padding: '8px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            <UsersIcon color={activeTab === 'User Statistics' ? '#007bff' : '#4d4d4d'} />
                            <Typography
                                sx={{
                                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                    fontWeight: activeTab === 'User Statistics' ? 500 : 400,
                                    fontFamily: activeTab === 'User Statistics' ? '"Roboto-Medium", "Roboto", sans-serif' : '"Roboto-Regular", "Roboto", sans-serif',
                                    color: activeTab === 'User Statistics' ? '#007bff' : '#4d4d4d',
                                    lineHeight: '20px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                User Statistics
                            </Typography>
                        </Box>

                        {/* Engagement Tab */}
                        <Box
                            onClick={() => setActiveTab('Engagement')}
                            sx={{
                                borderBottom: activeTab === 'Engagement' ? '2px solid #007bff' : 'none',
                                padding: '8px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            <ActivityIcon color={activeTab === 'Engagement' ? '#007bff' : '#4d4d4d'} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: activeTab === 'Engagement' ? 500 : 400,
                                    fontFamily: activeTab === 'Engagement' ? '"Roboto-Medium", "Roboto", sans-serif' : '"Roboto-Regular", "Roboto", sans-serif',
                                    color: activeTab === 'Engagement' ? '#007bff' : '#4d4d4d',
                                    lineHeight: '20px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Engagement
                            </Typography>
                        </Box>

                        {/* Projects Tab */}
                        <Box
                            onClick={() => setActiveTab('Projects')}
                            sx={{
                                borderBottom: activeTab === 'Projects' ? '2px solid #007bff' : 'none',
                                padding: '8px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            <FolderIcon color={activeTab === 'Projects' ? '#007bff' : '#4d4d4d'} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: activeTab === 'Projects' ? 500 : 400,
                                    fontFamily: activeTab === 'Projects' ? '"Roboto-Medium", "Roboto", sans-serif' : '"Roboto-Regular", "Roboto", sans-serif',
                                    color: activeTab === 'Projects' ? '#007bff' : '#4d4d4d',
                                    lineHeight: '20px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Projects
                            </Typography>
                        </Box>

                        {/* Machine Usage Tab */}
                        <Box
                            onClick={() => setActiveTab('Machine Usage')}
                            sx={{
                                borderBottom: activeTab === 'Machine Usage' ? '2px solid #007bff' : 'none',
                                padding: '8px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            <SettingsIcon color={activeTab === 'Machine Usage' ? '#007bff' : '#4d4d4d'} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: activeTab === 'Machine Usage' ? 500 : 400,
                                    fontFamily: activeTab === 'Machine Usage' ? '"Roboto-Medium", "Roboto", sans-serif' : '"Roboto-Regular", "Roboto", sans-serif',
                                    color: activeTab === 'Machine Usage' ? '#007bff' : '#4d4d4d',
                                    lineHeight: '20px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Machine Usage
                            </Typography>
                        </Box>

                        {/* MIS Data Tab */}
                        <Box
                            onClick={() => setActiveTab('MIS Data')}
                            sx={{
                                borderBottom: activeTab === 'MIS Data' ? '2px solid #007bff' : 'none',
                                padding: '8px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            <GlobeIcon color={activeTab === 'MIS Data' ? '#007bff' : '#4d4d4d'} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: activeTab === 'MIS Data' ? 500 : 400,
                                    fontFamily: activeTab === 'MIS Data' ? '"Roboto-Medium", "Roboto", sans-serif' : '"Roboto-Regular", "Roboto", sans-serif',
                                    color: activeTab === 'MIS Data' ? '#007bff' : '#4d4d4d',
                                    lineHeight: '20px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                MIS Data
                            </Typography>
                        </Box>

                        {/* Live Feed Tab */}
                        <Box
                            onClick={() => setActiveTab('Live Feed')}
                            sx={{
                                borderBottom: activeTab === 'Live Feed' ? '2px solid #007bff' : 'none',
                                padding: '8px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            <LiveCamIcon color={activeTab === 'Live Feed' ? '#007bff' : '#4d4d4d'} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: activeTab === 'Live Feed' ? 500 : 400,
                                    fontFamily: activeTab === 'Live Feed' ? '"Roboto-Medium", "Roboto", sans-serif' : '"Roboto-Regular", "Roboto", sans-serif',
                                    color: activeTab === 'Live Feed' ? '#007bff' : '#4d4d4d',
                                    lineHeight: '20px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Live Feed
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Tab Content */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: { xs: '8px', md: '12px' },
                        padding: { xs: '12px', sm: '16px', md: '24px' },
                        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                    }}
                >
                    {activeTab === 'User Statistics' && (
                        <UserStatisticsTab
                            key={`user-stats-${refreshKey}`}
                            selectedPeriod={selectedPeriod.toLowerCase()}
                        />
                    )}

                    {activeTab === 'Engagement' && (
                        <EngagementTab
                            key={`engagement-${refreshKey}`}
                            selectedPeriod={selectedPeriod.toLowerCase()}
                        />
                    )}

                    {activeTab === 'Projects' && (
                        <ProjectsTab
                            key={`projects-${refreshKey}`}
                            selectedPeriod={selectedPeriod.toLowerCase()}
                        />
                    )}

                    {activeTab === 'Machine Usage' && (
                        <MachineUsageTab
                            key={`machine-${refreshKey}`}
                            selectedPeriod={selectedPeriod.toLowerCase()}
                        />
                    )}

                    {activeTab === 'MIS Data' && (
                        <MISDataTab
                            key={`mis-${refreshKey}`}
                            selectedPeriod={selectedPeriod.toLowerCase()}
                        />
                    )}

                    {activeTab === 'Live Feed' && (
                        (() => {
                            let siteId = selectedSiteId;
                            if (!siteId) {
                                const tlId = user?.TL?.id || user?.TL_id;
                                siteId = typeof tlId === 'number' ? tlId : parseInt(tlId, 10);
                            }

                            if (!siteId || isNaN(siteId)) {
                                return (
                                    <Box sx={{ p: 5, textAlign: 'center' }}>
                                        <Typography variant="h6" color="error">
                                            Unable to load Live Feed - Invalid Site ID
                                        </Typography>
                                    </Box>
                                );
                            }

                            if (siteId === 2) {
                                return <TLIndividualLiveFeed siteId={2} />;
                            }
                            return <LiveSimulationTab siteId={siteId} />;
                        })()
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default TLDashboardContent;
